// ============================================================================
// AUTO-LAYOUT ENGINE - Organização Automática de Cards no Canvas
// ============================================================================
//
// Este módulo implementa algoritmos para organizar cards automaticamente:
// - Por Fluxo: Segue a ordem topológica das conexões
// - Horizontal: Alinha cards na mesma linha Y
// - Vertical: Alinha cards na mesma coluna X
// - Grid: Distribui em matriz
// - Compactar: Remove espaços vazios
// - Distribuir: Espaçamento uniforme
//
// ============================================================================

import { CardData, Connection } from '../types';
import { snapToGrid } from './calculations';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

export type LayoutStrategy = 
  | 'flow'          // Organiza baseado no fluxo (conexões)
  | 'horizontal'    // Alinha horizontalmente
  | 'vertical'      // Alinha verticalmente  
  | 'grid'          // Matriz N x M
  | 'compact'       // Compacta removendo espaços
  | 'distribute-h'  // Distribui horizontalmente com espaçamento uniforme
  | 'distribute-v'; // Distribui verticalmente com espaçamento uniforme

export interface AutoLayoutOptions {
  /** Estratégia de layout */
  strategy: LayoutStrategy;
  /** Espaçamento horizontal entre cards (pixels) */
  spacingX?: number;
  /** Espaçamento vertical entre cards (pixels) */
  spacingY?: number;
  /** Posição X inicial (default: mínimo X dos selecionados) */
  startX?: number;
  /** Posição Y inicial (default: mínimo Y dos selecionados) */
  startY?: number;
  /** Número de colunas para layout grid */
  gridCols?: number;
  /** Largura padrão do card */
  cardWidth?: number;
  /** Altura padrão do card */
  cardHeight?: number;
  /** Snap to grid */
  snapToGrid?: boolean;
  /** Tamanho do grid para snap */
  gridSize?: number;
  /** Manter card inicial fixo */
  keepInitialFixed?: boolean;
  /** Direção do fluxo: esquerda-direita ou cima-baixo */
  flowDirection?: 'LR' | 'TB';
}

export interface LayoutResult {
  /** Cards com posições atualizadas */
  cards: CardData[];
  /** Estatísticas do layout */
  stats: {
    /** Número de cards organizados */
    cardsOrganized: number;
    /** Largura total ocupada */
    totalWidth: number;
    /** Altura total ocupada */
    totalHeight: number;
    /** Níveis/colunas identificados */
    levels: number;
  };
}

// ============================================================================
// CONSTANTES
// ============================================================================

const DEFAULT_SPACING_X = 280;
const DEFAULT_SPACING_Y = 200;
const DEFAULT_CARD_WIDTH = 240;
const DEFAULT_CARD_HEIGHT = 180; // Altura real renderizada dos cards (card height = 180px)
const DECISION_SIZE = 160;
const DEFAULT_GRID_SIZE = 20;

// Espaçamento extra para decisões em cascata vertical (evitar sobreposição de labels)
const DECISION_CASCADE_SPACING_Y = 220; // Maior que DECISION_SIZE para caber as labels
const DECISION_LABEL_HEIGHT = 40; // Altura estimada das labels (Sim/Não)

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

/**
 * Obtém dimensões do card baseado no tipo
 */
function getCardDimensions(card: CardData): { width: number; height: number } {
  if (card.tipo === 'decisao') {
    return { width: DECISION_SIZE, height: DECISION_SIZE };
  }
  return { 
    width: card.calculatedWidth || DEFAULT_CARD_WIDTH, 
    height: DEFAULT_CARD_HEIGHT 
  };
}

/**
 * Ordena cards por posição X (esquerda para direita)
 */
function sortByPositionX(cards: CardData[]): CardData[] {
  return [...cards].sort((a, b) => a.posicao.x - b.posicao.x);
}

/**
 * Ordena cards por posição Y (cima para baixo)
 */
function sortByPositionY(cards: CardData[]): CardData[] {
  return [...cards].sort((a, b) => a.posicao.y - b.posicao.y);
}

/**
 * Encontra o bounding box de um conjunto de cards
 */
function getBoundingBox(cards: CardData[]): { minX: number; maxX: number; minY: number; maxY: number } {
  if (cards.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }
  
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  
  cards.forEach(card => {
    const { width, height } = getCardDimensions(card);
    minX = Math.min(minX, card.posicao.x);
    maxX = Math.max(maxX, card.posicao.x + width);
    minY = Math.min(minY, card.posicao.y);
    maxY = Math.max(maxY, card.posicao.y + height);
  });
  
  return { minX, maxX, minY, maxY };
}

/**
 * Aplica snap to grid se habilitado
 */
function applySnap(x: number, y: number, options: AutoLayoutOptions): { x: number; y: number } {
  if (options.snapToGrid !== false) {
    return snapToGrid(x, y, options.gridSize || DEFAULT_GRID_SIZE);
  }
  return { x, y };
}

// ============================================================================
// DECISÕES EM CASCATA VERTICAL
// ============================================================================

/**
 * Identifica cadeias de decisões conectadas verticalmente (via saída 'Não')
 * Uma cascata é: Decisão A --[Não/bottom]--> Decisão B --[Não/bottom]--> Decisão C
 */
function identifyDecisionCascades(cards: CardData[]): Map<string, string[]> {
  const cascades = new Map<string, string[]>();
  const cardMap = new Map(cards.map(c => [c.id, c]));
  const visited = new Set<string>();
  
  // Encontrar decisões que são início de cascata (não são alvo de outra decisão via bottom)
  const isTargetOfBottomConnection = new Set<string>();
  cards.forEach(card => {
    if (card.tipo === 'decisao') {
      (card.conexoes_saida ?? []).forEach(conn => {
        if (conn.sourceHandle === 'bottom') {
          const target = cardMap.get(conn.targetId);
          if (target?.tipo === 'decisao') {
            isTargetOfBottomConnection.add(conn.targetId);
          }
        }
      });
    }
  });
  
  // Para cada decisão que inicia uma cascata, seguir a cadeia
  cards.forEach(card => {
    if (card.tipo === 'decisao' && !isTargetOfBottomConnection.has(card.id) && !visited.has(card.id)) {
      const chain: string[] = [card.id];
      visited.add(card.id);
      
      let current = card;
      while (current) {
        // Encontrar próxima decisão conectada via bottom
        const bottomConnection = (current.conexoes_saida ?? []).find(conn => conn.sourceHandle === 'bottom');
        if (!bottomConnection) break;
        
        const nextCard = cardMap.get(bottomConnection.targetId);
        if (!nextCard || nextCard.tipo !== 'decisao' || visited.has(nextCard.id)) break;
        
        chain.push(nextCard.id);
        visited.add(nextCard.id);
        current = nextCard;
      }
      
      // Só registrar se houver mais de uma decisão na cadeia
      if (chain.length > 1) {
        cascades.set(card.id, chain);
      }
    }
  });
  
  return cascades;
}

/**
 * Ajusta conexões para cascatas de decisões:
 * - Saída 'Não' (bottom) de decisão para outra decisão: targetHandle = 'top'
 * - Saída 'Sim' (right) permanece: targetHandle = 'left' (padrão)
 */
export function adjustDecisionCascadeConnections(cards: CardData[]): CardData[] {
  const cardMap = new Map(cards.map(c => [c.id, c]));
  
  return cards.map(card => {
    if (card.tipo !== 'decisao') return card;
    
    const updatedConnections = (card.conexoes_saida ?? []).map(conn => {
      const targetCard = cardMap.get(conn.targetId);
      
      // Se é saída 'Não' (bottom) para outra decisão, entrada deve ser pelo 'top'
      if (conn.sourceHandle === 'bottom' && targetCard?.tipo === 'decisao') {
        return {
          ...conn,
          targetHandle: 'top' as const
        };
      }
      
      // Saída 'Sim' (right) ou outras: entrada pela esquerda (padrão)
      if (!conn.targetHandle) {
        return {
          ...conn,
          targetHandle: 'left' as const
        };
      }
      
      return conn;
    });
    
    return {
      ...card,
      conexoes_saida: updatedConnections
    };
  });
}

/**
 * Posiciona decisões em cascata vertical com espaçamento adequado
 */
function layoutDecisionCascades(
  cards: CardData[],
  cascades: Map<string, string[]>,
  options: AutoLayoutOptions
): CardData[] {
  const cardMap = new Map(cards.map(c => [c.id, c]));
  const positionedIds = new Set<string>();
  const result = new Map<string, CardData>();
  
  // Inicializar com cards originais
  cards.forEach(c => result.set(c.id, c));
  
  // Posicionar cada cascata
  cascades.forEach((chain, startId) => {
    const startCard = cardMap.get(startId);
    if (!startCard) return;
    
    // Manter posição X do primeiro card (alinhamento vertical)
    const baseX = startCard.posicao.x + (DECISION_SIZE / 2); // Centro do diamante
    let currentY = startCard.posicao.y;
    
    chain.forEach((cardId, index) => {
      const card = cardMap.get(cardId);
      if (!card) return;
      
      // Primeira decisão mantém posição Y original
      // Subsequentes são posicionadas abaixo com espaçamento adequado
      const newY = index === 0 
        ? currentY 
        : currentY + DECISION_CASCADE_SPACING_Y;
      
      // Alinhar centro do diamante com o primeiro da cascata
      const newX = baseX - (DECISION_SIZE / 2);
      
      const snapped = applySnap(newX, newY, options);
      
      result.set(cardId, {
        ...card,
        posicao: snapped
      });
      
      positionedIds.add(cardId);
      currentY = newY;
    });
  });
  
  return Array.from(result.values());
}

// ============================================================================
// ALGORITMO: ORDENAÇÃO TOPOLÓGICA (Para layout por fluxo)
// ============================================================================

/**
 * Realiza ordenação topológica baseada nas conexões
 * Retorna cards ordenados do início ao fim do fluxo
 */
function topologicalSort(cards: CardData[], allCards: CardData[]): CardData[] {
  const cardMap = new Map(cards.map(c => [c.id, c]));
  const allCardsMap = new Map(allCards.map(c => [c.id, c]));
  const selectedIds = new Set(cards.map(c => c.id));
  
  // Construir grafo de adjacência (apenas entre cards selecionados)
  const inDegree = new Map<string, number>();
  const adjacencyList = new Map<string, string[]>();
  
  cards.forEach(card => {
    inDegree.set(card.id, 0);
    adjacencyList.set(card.id, []);
  });
  
  // Calcular in-degree e adjacências
  allCards.forEach(card => {
    (card.conexoes_saida ?? []).forEach(conn => {
      // Só considerar conexões entre cards selecionados
      if (selectedIds.has(card.id) && selectedIds.has(conn.targetId)) {
        adjacencyList.get(card.id)?.push(conn.targetId);
        inDegree.set(conn.targetId, (inDegree.get(conn.targetId) || 0) + 1);
      }
    });
  });
  
  // Fila inicial: cards sem predecessores selecionados
  const queue: string[] = [];
  cards.forEach(card => {
    if (inDegree.get(card.id) === 0) {
      queue.push(card.id);
    }
  });
  
  // Ordenação topológica (Kahn's algorithm)
  const sorted: CardData[] = [];
  
  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    const card = cardMap.get(nodeId);
    if (card) sorted.push(card);
    
    adjacencyList.get(nodeId)?.forEach(neighborId => {
      const newDegree = (inDegree.get(neighborId) || 0) - 1;
      inDegree.set(neighborId, newDegree);
      if (newDegree === 0) {
        queue.push(neighborId);
      }
    });
  }
  
  // Adicionar cards não conectados (que não entraram na ordenação)
  cards.forEach(card => {
    if (!sorted.find(c => c.id === card.id)) {
      sorted.push(card);
    }
  });
  
  return sorted;
}

const VERTICAL_SPACING_NORMAL = 220;
const VERTICAL_SPACING_BRANCH = 350;
const INITIAL_CARD_WIDTH = 220;
const INITIAL_CARD_GAP = 40;

/**
 * Níveis de fluxo usando BFS de primeira visita a partir do início.
 * Arestas de retorno (alvo já visitado) não avançam coluna — evita empurrar
 * cards para a direita e reduz cruzamento de setas.
 */
function assignForwardFlowLevels(
  cards: CardData[],
  allCards: CardData[],
): Map<string, number> {
  const selectedIds = new Set(cards.map(c => c.id));
  const cardById = new Map<string, CardData>();
  allCards.forEach(c => cardById.set(c.id, c));
  cards.forEach(c => cardById.set(c.id, c));

  const levels = new Map<string, number>();
  const roots: CardData[] = [];

  const initial = cards.find(c => c.tipo === 'card_inicial');
  if (initial) {
    roots.push(initial);
  } else {
    const hasIncoming = new Set<string>();
    allCards.forEach(card => {
      (card.conexoes_saida ?? []).forEach(conn => {
        if (selectedIds.has(card.id) && selectedIds.has(conn.targetId)) {
          hasIncoming.add(conn.targetId);
        }
      });
    });
    cards.filter(c => !hasIncoming.has(c.id)).forEach(c => roots.push(c));
  }

  if (roots.length === 0 && cards.length > 0) {
    roots.push([...cards].sort((a, b) => a.posicao.x - b.posicao.x || a.posicao.y - b.posicao.y)[0]);
  }

  const queue: string[] = [];
  roots.forEach(root => {
    if (!levels.has(root.id)) {
      levels.set(root.id, 0);
      queue.push(root.id);
    }
  });

  while (queue.length > 0) {
    const id = queue.shift()!;
    const level = levels.get(id) ?? 0;
    const card = cardById.get(id);
    if (!card) continue;

    (card.conexoes_saida ?? []).forEach(conn => {
      if (!selectedIds.has(conn.targetId)) return;
      if (levels.has(conn.targetId)) return;
      levels.set(conn.targetId, level + 1);
      queue.push(conn.targetId);
    });
  }

  let maxLevel = Math.max(0, ...Array.from(levels.values()));
  cards.forEach(card => {
    if (levels.has(card.id)) return;
    let inferred = maxLevel;
    allCards.forEach(src => {
      (src.conexoes_saida ?? []).forEach(conn => {
        if (conn.targetId !== card.id || !selectedIds.has(src.id)) return;
        const srcLevel = levels.get(src.id);
        if (srcLevel !== undefined) inferred = Math.min(inferred, srcLevel);
      });
    });
    levels.set(card.id, inferred);
  });

  return levels;
}

function classifyFlowBranches(
  allCards: CardData[],
  selectedIds: Set<string>,
): { cameFromBottom: Set<string>; isSecondaryBranch: Set<string> } {
  const cameFromBottom = new Set<string>();
  const isSecondaryBranch = new Set<string>();

  allCards.forEach(card => {
    if (!selectedIds.has(card.id)) return;
    (card.conexoes_saida ?? []).forEach((conn, connIndex) => {
      if (!selectedIds.has(conn.targetId)) return;
      if (conn.sourceHandle === 'bottom') cameFromBottom.add(conn.targetId);
      if (connIndex > 0) isSecondaryBranch.add(conn.targetId);
    });
  });

  return { cameFromBottom, isSecondaryBranch };
}

function orderLevelByBarycenter(
  levelCards: CardData[],
  level: number,
  levels: Map<string, number>,
  allCards: CardData[],
  selectedIds: Set<string>,
  orderIndex: Map<string, number>,
): CardData[] {
  const getBarycenter = (cardId: string): number => {
    let sum = 0;
    let count = 0;
    allCards.forEach(src => {
      if (!selectedIds.has(src.id)) return;
      (src.conexoes_saida ?? []).forEach(conn => {
        if (conn.targetId !== cardId || !selectedIds.has(conn.targetId)) return;
        const srcLevel = levels.get(src.id);
        if (srcLevel === undefined || srcLevel >= level) return;
        const idx = orderIndex.get(src.id);
        if (idx !== undefined) {
          sum += idx;
          count += 1;
        }
      });
    });
    if (count > 0) return sum / count;
    return orderIndex.get(cardId) ?? 0;
  };

  return [...levelCards].sort((a, b) => {
    const ba = getBarycenter(a.id);
    const bb = getBarycenter(b.id);
    if (ba !== bb) return ba - bb;
    return (orderIndex.get(a.id) ?? 0) - (orderIndex.get(b.id) ?? 0);
  });
}

/**
 * Layout por trilhas — mesma lógica da formação inicial (importação Excel):
 * trilha principal horizontal, bifurcações (Não / paralelo) abaixo com maior espaçamento.
 */
function layoutByFlow(
  cards: CardData[],
  allCards: CardData[],
  options: AutoLayoutOptions,
): CardData[] {
  if (cards.length === 0) return cards;

  const spacingX = options.spacingX || DEFAULT_SPACING_X;
  const selectedIds = new Set(cards.map(c => c.id));

  const working = cards.map(c => ({ ...c, posicao: { ...c.posicao } }));
  const cardById = new Map(working.map(c => [c.id, c]));

  const hasConnections = working.some(c => (c.conexoes_saida ?? []).length > 0);
  if (!hasConnections) {
    return layoutHorizontal(working, options);
  }

  const levels = assignForwardFlowLevels(working, allCards);
  const { cameFromBottom, isSecondaryBranch } = classifyFlowBranches(allCards, selectedIds);

  const anchorCards = working.filter(
    c => !cameFromBottom.has(c.id) && !isSecondaryBranch.has(c.id),
  );
  const anchorBox = getBoundingBox(anchorCards.length > 0 ? anchorCards : working);
  const startX = options.startX ?? anchorBox.minX;
  const startY = options.startY ?? anchorBox.minY;

  const cardsByLevel = new Map<number, CardData[]>();
  working.forEach(card => {
    const level = levels.get(card.id) ?? 0;
    if (!cardsByLevel.has(level)) cardsByLevel.set(level, []);
    cardsByLevel.get(level)!.push(card);
  });

  const sortedLevels = Array.from(cardsByLevel.keys()).sort((a, b) => a - b);
  const orderIndex = new Map<string, number>();
  working.forEach((c, i) => orderIndex.set(c.id, i));

  sortedLevels.forEach(level => {
    const ordered = orderLevelByBarycenter(
      cardsByLevel.get(level) ?? [],
      level,
      levels,
      allCards,
      selectedIds,
      orderIndex,
    );
    ordered.forEach((c, i) => orderIndex.set(c.id, i));
    cardsByLevel.set(level, ordered);
  });

  const occupiedPositions = new Map<string, boolean>();

  sortedLevels.forEach(level => {
    const levelCards = cardsByLevel.get(level) ?? [];
    const mainTrackCards: CardData[] = [];
    const branchCards: CardData[] = [];

    levelCards.forEach(card => {
      if (cameFromBottom.has(card.id) || isSecondaryBranch.has(card.id)) {
        branchCards.push(card);
      } else {
        mainTrackCards.push(card);
      }
    });

    const placeCard = (card: CardData, baseY: number, branchIndex: number, isBranch: boolean) => {
      let targetY = isBranch
        ? baseY + VERTICAL_SPACING_BRANCH + branchIndex * VERTICAL_SPACING_NORMAL
        : baseY;

      let finalY = targetY;
      let attempts = 0;
      while (occupiedPositions.has(`${level}_${finalY}`) && attempts < 12) {
        finalY += VERTICAL_SPACING_NORMAL;
        attempts += 1;
      }

      const snapped = applySnap(startX + level * spacingX, finalY, options);
      card.posicao = snapped;
      occupiedPositions.set(`${level}_${finalY}`, true);
    };

    mainTrackCards.forEach((card, idx) => {
      const predecessors = working.filter(c => {
        const srcLevel = levels.get(c.id) ?? 999;
        if (srcLevel >= level) return false;
        return (c.conexoes_saida ?? []).some(conn => conn.targetId === card.id);
      });
      let targetY = startY;
      if (predecessors.length > 0) {
        const mainPred = predecessors.find(p => p.posicao?.y !== undefined);
        if (mainPred?.posicao) targetY = mainPred.posicao.y;
      }
      placeCard(card, targetY, idx, false);
    });

    branchCards.forEach((card, idx) => {
      const predecessors = working.filter(c => {
        const srcLevel = levels.get(c.id) ?? 999;
        if (srcLevel >= level) return false;
        return (c.conexoes_saida ?? []).some(conn => conn.targetId === card.id);
      });
      let baseY = startY;
      if (predecessors.length > 0) {
        const pred = predecessors.find(p => p.posicao?.y !== undefined);
        if (pred?.posicao) baseY = pred.posicao.y;
      }
      placeCard(card, baseY, idx, true);
    });
  });

  return working.map(c => cardById.get(c.id)!);
}

function groupByLevel(cards: CardData[], allCards: CardData[]): Map<number, CardData[]> {
  const levels = assignForwardFlowLevels(cards, allCards);
  const grouped = new Map<number, CardData[]>();

  cards.forEach(card => {
    const level = levels.get(card.id) ?? 0;
    if (!grouped.has(level)) grouped.set(level, []);
    grouped.get(level)!.push(card);
  });

  return grouped;
}

/**
 * Layout Horizontal - Alinha todos na mesma linha Y
 */
function layoutHorizontal(cards: CardData[], options: AutoLayoutOptions): CardData[] {
  const spacingX = options.spacingX || DEFAULT_SPACING_X;
  const sorted = sortByPositionX(cards);
  const bbox = getBoundingBox(cards);
  const startX = options.startX ?? bbox.minX;
  const startY = options.startY ?? bbox.minY;
  
  return sorted.map((card, index) => {
    const newX = startX + index * spacingX;
    const snapped = applySnap(newX, startY, options);
    
    return {
      ...card,
      posicao: snapped,
    };
  });
}

/**
 * Layout Vertical - Alinha todos na mesma coluna X
 */
function layoutVertical(cards: CardData[], options: AutoLayoutOptions): CardData[] {
  const spacingY = options.spacingY || DEFAULT_SPACING_Y;
  const sorted = sortByPositionY(cards);
  const bbox = getBoundingBox(cards);
  const startX = options.startX ?? bbox.minX;
  const startY = options.startY ?? bbox.minY;
  
  return sorted.map((card, index) => {
    const newY = startY + index * spacingY;
    const snapped = applySnap(startX, newY, options);
    
    return {
      ...card,
      posicao: snapped,
    };
  });
}

/**
 * Layout Grid - Distribui em matriz
 */
function layoutGrid(cards: CardData[], options: AutoLayoutOptions): CardData[] {
  const spacingX = options.spacingX || DEFAULT_SPACING_X;
  const spacingY = options.spacingY || DEFAULT_SPACING_Y;
  const gridCols = options.gridCols || Math.ceil(Math.sqrt(cards.length));
  const bbox = getBoundingBox(cards);
  const startX = options.startX ?? bbox.minX;
  const startY = options.startY ?? bbox.minY;
  
  // Ordenar por posição original para manter alguma lógica
  const sorted = sortByPositionX(cards);
  
  return sorted.map((card, index) => {
    const row = Math.floor(index / gridCols);
    const col = index % gridCols;
    const newX = startX + col * spacingX;
    const newY = startY + row * spacingY;
    const snapped = applySnap(newX, newY, options);
    
    return {
      ...card,
      posicao: snapped,
    };
  });
}

/**
 * Compactar - Remove espaços vazios mantendo ordem relativa
 */
function layoutCompact(cards: CardData[], options: AutoLayoutOptions): CardData[] {
  const spacingX = options.spacingX || 60; // Menor espaçamento para compactar
  const spacingY = options.spacingY || 40;
  const bbox = getBoundingBox(cards);
  const startX = options.startX ?? bbox.minX;
  const startY = options.startY ?? bbox.minY;
  
  // Agrupar por linhas (Y similar)
  const yTolerance = 50;
  const rows: CardData[][] = [];
  
  const sorted = sortByPositionY(cards);
  sorted.forEach(card => {
    // Encontrar linha existente com Y próximo
    const existingRow = rows.find(row => 
      Math.abs(row[0].posicao.y - card.posicao.y) < yTolerance
    );
    
    if (existingRow) {
      existingRow.push(card);
    } else {
      rows.push([card]);
    }
  });
  
  // Ordenar cards dentro de cada linha por X
  rows.forEach(row => row.sort((a, b) => a.posicao.x - b.posicao.x));
  
  // Reposicionar
  const result: CardData[] = [];
  let currentY = startY;
  
  rows.forEach(row => {
    let currentX = startX;
    let maxHeight = 0;
    
    row.forEach(card => {
      const { width, height } = getCardDimensions(card);
      const snapped = applySnap(currentX, currentY, options);
      
      result.push({
        ...card,
        posicao: snapped,
      });
      
      currentX += width + spacingX;
      maxHeight = Math.max(maxHeight, height);
    });
    
    currentY += maxHeight + spacingY;
  });
  
  return result;
}

/**
 * Distribuir Horizontalmente - Espaçamento uniforme mantendo Y
 */
function distributeHorizontal(cards: CardData[], options: AutoLayoutOptions): CardData[] {
  if (cards.length < 2) return cards;
  
  const sorted = sortByPositionX(cards);
  const bbox = getBoundingBox(cards);
  const startX = options.startX ?? bbox.minX;
  const endX = options.startX !== undefined 
    ? startX + (cards.length - 1) * (options.spacingX || DEFAULT_SPACING_X)
    : bbox.maxX - getCardDimensions(sorted[sorted.length - 1]).width;
  
  const totalSpace = endX - startX;
  const spacing = totalSpace / (cards.length - 1);
  
  return sorted.map((card, index) => {
    const newX = startX + index * spacing;
    const snapped = applySnap(newX, card.posicao.y, options);
    
    return {
      ...card,
      posicao: { x: snapped.x, y: card.posicao.y }, // Mantém Y original
    };
  });
}

/**
 * Distribuir Verticalmente - Espaçamento uniforme mantendo X
 */
function distributeVertical(cards: CardData[], options: AutoLayoutOptions): CardData[] {
  if (cards.length < 2) return cards;
  
  const sorted = sortByPositionY(cards);
  const bbox = getBoundingBox(cards);
  const startY = options.startY ?? bbox.minY;
  const endY = options.startY !== undefined
    ? startY + (cards.length - 1) * (options.spacingY || DEFAULT_SPACING_Y)
    : bbox.maxY - getCardDimensions(sorted[sorted.length - 1]).height;
  
  const totalSpace = endY - startY;
  const spacing = totalSpace / (cards.length - 1);
  
  return sorted.map((card, index) => {
    const newY = startY + index * spacing;
    const snapped = applySnap(card.posicao.x, newY, options);
    
    return {
      ...card,
      posicao: { x: card.posicao.x, y: snapped.y }, // Mantém X original
    };
  });
}

// ============================================================================
// SWIMLANES - Reorganização por Raias (Agrupamento por Metadado)
// ============================================================================

export type SwimLaneGroupBy = 'setor' | 'quem';

/** Altura fixa de cada raia (pixels) */
export const SWIMLANE_HEIGHT = 220;
/** Espaçamento entre raias (pixels) */
export const SWIMLANE_GAP = 20;
/** Largura do label lateral */
export const SWIMLANE_LABEL_WIDTH = 120;

export interface SwimLaneOptions {
  /** Campo de agrupamento */
  groupBy: SwimLaneGroupBy;
  /** Espaçamento horizontal entre colunas (pixels) */
  spacingX?: number;
  /** Snap to grid */
  snapToGrid?: boolean;
  /** Tamanho do grid para snap */
  gridSize?: number;
}

export interface SwimLaneInfo {
  /** Nome da raia */
  label: string;
  /** Posição Y do topo da raia */
  y: number;
  /** Altura fixa da raia */
  height: number;
  /** Centro Y da raia (ponto de snap) */
  centerY: number;
  /** Quantidade de cards na raia */
  cardCount: number;
}

export interface SwimLaneResult {
  /** Cards com posições recalculadas */
  cards: CardData[];
  /** Informações de cada raia criada */
  lanes: SwimLaneInfo[];
  /** Estatísticas */
  stats: {
    cardsOrganized: number;
    totalWidth: number;
    totalHeight: number;
    laneCount: number;
  };
}

/**
 * Reorganiza cards em raias horizontais (swimlanes) agrupadas por um atributo.
 *
 * Algoritmo:
 * 1. Agrupa cards pelo campo selecionado (quem/setor)
 * 2. Determina ordem horizontal via nível topológico (fluxo)
 * 3. Cria bandas horizontais fixas de 160px — cada grupo recebe uma raia
 * 4. Posiciona cards: X pelo nível do fluxo, Y no centro da raia
 * 5. NÃO modifica conexões — apenas reposiciona
 */
export function layoutBySwimlanes(
  cards: CardData[],
  allCards: CardData[],
  options: SwimLaneOptions
): SwimLaneResult {
  if (cards.length === 0) {
    return {
      cards: [],
      lanes: [],
      stats: { cardsOrganized: 0, totalWidth: 0, totalHeight: 0, laneCount: 0 },
    };
  }

  // Separar card inicial (fica fora das raias)
  const initialCard = cards.find(c => c.tipo === 'card_inicial');
  const workCards = cards.filter(c => c.tipo !== 'card_inicial');

  // 1. Agrupar pelo atributo selecionado
  const groups = new Map<string, CardData[]>();
  workCards.forEach(card => {
    const value = card[options.groupBy] || 'Sem atribuição';
    if (!groups.has(value)) groups.set(value, []);
    groups.get(value)!.push(card);
  });

  // 2. Calcular nível topológico de cada card (posição horizontal no fluxo)
  const levelMap = new Map<string, number>();
  const levelGroups = groupByLevel(workCards, allCards);
  levelGroups.forEach((cardsInLevel, level) => {
    cardsInLevel.forEach(card => levelMap.set(card.id, level));
  });

  // 3. Parâmetros de layout
  const spacingX = options.spacingX || DEFAULT_SPACING_X;
  // Reservar espaço para o card inicial à esquerda dos cards de nível 0
  // card_inicial renderiza com width=220 (Card.tsx: isInitial sempre recebe 220)
  const INITIAL_CARD_WIDTH = 220;
  const INITIAL_CARD_GAP = 20;
  const startX = SWIMLANE_LABEL_WIDTH + 40 + INITIAL_CARD_WIDTH + INITIAL_CARD_GAP; // label + margin + espaço card inicial
  const startY = 60;

  const result: CardData[] = [];
  const lanes: SwimLaneInfo[] = [];
  let currentY = startY;

  // Ordenar grupos pela ordem do fluxo (menor nível topológico do grupo)
  const sortedGroups = Array.from(groups.entries()).sort((a, b) => {
    const minLevelA = Math.min(...a[1].map(c => levelMap.get(c.id) ?? 999));
    const minLevelB = Math.min(...b[1].map(c => levelMap.get(c.id) ?? 999));
    if (minLevelA !== minLevelB) return minLevelA - minLevelB;
    return a[0].localeCompare(b[0], 'pt-BR');
  });

  const PARALLEL_CARD_GAP = 20; // gap vertical entre cards paralelos dentro da mesma raia

  sortedGroups.forEach(([label, groupCards]) => {
    // Ordenar cards na raia: por nível de fluxo, depois por Y original
    groupCards.sort((a, b) => {
      const levelA = levelMap.get(a.id) ?? 999;
      const levelB = levelMap.get(b.id) ?? 999;
      if (levelA !== levelB) return levelA - levelB;
      return a.posicao.y - b.posicao.y;
    });

    // Agrupar cards por nível BFS dentro desta raia (detectar paralelos)
    const byLevel = new Map<number, CardData[]>();
    groupCards.forEach(card => {
      const lv = levelMap.get(card.id) ?? 999;
      if (!byLevel.has(lv)) byLevel.set(lv, []);
      byLevel.get(lv)!.push(card);
    });

    // Calcular altura necessária: stack vertical mais alto entre todos os níveis
    let maxStackHeight = 0;
    byLevel.forEach(cardsAtLevel => {
      const stackH = cardsAtLevel.reduce((sum, c) => sum + getCardDimensions(c).height, 0)
        + (cardsAtLevel.length - 1) * PARALLEL_CARD_GAP;
      if (stackH > maxStackHeight) maxStackHeight = stackH;
    });

    // Padding vertical acima/abaixo do stack
    const LANE_PADDING = 40;
    const laneHeight = Math.max(SWIMLANE_HEIGHT, maxStackHeight + LANE_PADDING * 2);
    const laneCenter = currentY + laneHeight / 2;

    // Posicionar cards: cards paralelos distribuídos verticalmente em volta do centro
    byLevel.forEach((cardsAtLevel, level) => {
      const x = startX + level * spacingX;
      const stackH = cardsAtLevel.reduce((sum, c) => sum + getCardDimensions(c).height, 0)
        + (cardsAtLevel.length - 1) * PARALLEL_CARD_GAP;
      let offsetY = laneCenter - stackH / 2;

      cardsAtLevel.forEach(card => {
        const { height } = getCardDimensions(card);
        const snapped = applySnap(x, offsetY, {
          strategy: 'flow',
          snapToGrid: options.snapToGrid,
          gridSize: options.gridSize,
        });
        result.push({ ...card, posicao: snapped });
        offsetY += height + PARALLEL_CARD_GAP;
      });
    });

    lanes.push({
      label,
      y: currentY,
      height: laneHeight,
      centerY: laneCenter,
      cardCount: groupCards.length,
    });

    currentY += laneHeight + SWIMLANE_GAP;
  });

  // Posicionar card inicial na mesma raia do primeiro card do fluxo
  if (initialCard) {
    // Cards de nível 0 são os que não têm predecessores entre os workCards
    // (i.e., os que o card_inicial conecta diretamente)
    const level0Ids = new Set<string>(
      (levelGroups.get(0) ?? []).map(c => c.id)
    );
    let targetLaneCenter = lanes.length > 0 ? lanes[0].centerY : startY + SWIMLANE_HEIGHT / 2;
    let found = false;
    sortedGroups.forEach(([, groupCards], laneIndex) => {
      if (!found && groupCards.some(c => level0Ids.has(c.id))) {
        targetLaneCenter = lanes[laneIndex]?.centerY ?? targetLaneCenter;
        found = true;
      }
    });
    const { height } = getCardDimensions(initialCard);
    // Usar INITIAL_CARD_WIDTH (220) para o X — card_inicial é sempre 220px de largura
    const snapped = applySnap(startX - INITIAL_CARD_WIDTH - INITIAL_CARD_GAP, targetLaneCenter - height / 2, {
      strategy: 'flow',
      snapToGrid: options.snapToGrid,
      gridSize: options.gridSize,
    });
    result.unshift({
      ...initialCard,
      posicao: snapped,
    });
  }

  const bbox = getBoundingBox(result);

  return {
    cards: result,
    lanes,
    stats: {
      cardsOrganized: result.length,
      totalWidth: bbox.maxX - bbox.minX,
      totalHeight: bbox.maxY - bbox.minY,
      laneCount: lanes.length,
    },
  };
}

/**
 * Encontra a raia mais próxima para snap vertical
 */
export function snapToNearestLane(y: number, cardHeight: number, lanes: SwimLaneInfo[]): number {
  if (lanes.length === 0) return y;
  
  const cardCenter = y + cardHeight / 2;
  let closestLane = lanes[0];
  let closestDist = Math.abs(cardCenter - closestLane.centerY);

  for (let i = 1; i < lanes.length; i++) {
    const dist = Math.abs(cardCenter - lanes[i].centerY);
    if (dist < closestDist) {
      closestDist = dist;
      closestLane = lanes[i];
    }
  }

  // Threshold = metade da altura da raia mais próxima (snap dentro dos limites da raia)
  const threshold = closestLane.height / 2;
  if (closestDist < threshold) {
    return closestLane.centerY - cardHeight / 2;
  }
  return y;
}

// ============================================================================
// FUNÇÃO PRINCIPAL
// ============================================================================

/**
 * Organiza automaticamente um conjunto de cards selecionados
 * 
 * @param selectedCards - Cards a serem organizados
 * @param allCards - Todos os cards (para referência de conexões)
 * @param options - Opções de layout
 * @returns Resultado com cards atualizados e estatísticas
 */
export function autoLayoutCards(
  selectedCards: CardData[],
  allCards: CardData[],
  options: AutoLayoutOptions
): LayoutResult {
  if (selectedCards.length === 0) {
    return {
      cards: [],
      stats: { cardsOrganized: 0, totalWidth: 0, totalHeight: 0, levels: 0 },
    };
  }

  // Separar card inicial se necessário
  let cardsToLayout = selectedCards;
  let initialCard: CardData | undefined;
  
  if (options.keepInitialFixed) {
    initialCard = selectedCards.find(c => c.tipo === 'card_inicial');
    cardsToLayout = selectedCards.filter(c => c.tipo !== 'card_inicial');
  }

  let layoutedCards: CardData[];

  switch (options.strategy) {
    case 'flow':
      layoutedCards = layoutByFlow(cardsToLayout, allCards, options);
      break;
    case 'horizontal':
      layoutedCards = layoutHorizontal(cardsToLayout, options);
      break;
    case 'vertical':
      layoutedCards = layoutVertical(cardsToLayout, options);
      break;
    case 'grid':
      layoutedCards = layoutGrid(cardsToLayout, options);
      break;
    case 'compact':
      layoutedCards = layoutCompact(cardsToLayout, options);
      break;
    case 'distribute-h':
      layoutedCards = distributeHorizontal(cardsToLayout, options);
      break;
    case 'distribute-v':
      layoutedCards = distributeVertical(cardsToLayout, options);
      break;
    default:
      layoutedCards = cardsToLayout;
  }

  // ============================================================================
  // AJUSTE DE DECISÕES EM CASCATA VERTICAL
  // Identifica cadeias de decisões conectadas via 'Não' e ajusta:
  // 1. Espaçamento vertical adequado para não sobrepor labels
  // 2. targetHandle = 'top' para entrada vinda de decisão acima
  // ============================================================================
  const cascades = identifyDecisionCascades(layoutedCards);
  if (cascades.size > 0) {
    // Aplicar posicionamento vertical para cascatas
    layoutedCards = layoutDecisionCascades(layoutedCards, cascades, options);
    // Ajustar conexões (targetHandle)
    layoutedCards = adjustDecisionCascadeConnections(layoutedCards);
  }

  // Re-adicionar card inicial à esquerda da trilha principal
  if (initialCard) {
    const bbox = getBoundingBox(layoutedCards);
    const spacingX = options.spacingX || DEFAULT_SPACING_X;
    const mainY = layoutedCards.length > 0
      ? Math.min(...layoutedCards.map(c => c.posicao.y))
      : (options.startY ?? bbox.minY);
    const firstActivityX = layoutedCards.length > 0
      ? Math.min(...layoutedCards.map(c => c.posicao.x))
      : (options.startX ?? bbox.minX) + spacingX;
    const snapped = applySnap(
      firstActivityX - INITIAL_CARD_WIDTH - INITIAL_CARD_GAP,
      mainY,
      options,
    );
    layoutedCards = [{ ...initialCard, posicao: snapped }, ...layoutedCards];
  }

  // Calcular estatísticas
  const bbox = getBoundingBox(layoutedCards);
  const levelGroups = groupByLevel(cardsToLayout, allCards);
  
  return {
    cards: layoutedCards,
    stats: {
      cardsOrganized: layoutedCards.length,
      totalWidth: bbox.maxX - bbox.minX,
      totalHeight: bbox.maxY - bbox.minY,
      levels: levelGroups.size,
    },
  };
}

// ============================================================================
// FUNÇÕES DE CONVENIÊNCIA
// ============================================================================

/**
 * Alinha cards selecionados à esquerda (mesmo X)
 */
export function alignLeft(cards: CardData[]): CardData[] {
  if (cards.length === 0) return cards;
  const minX = Math.min(...cards.map(c => c.posicao.x));
  return cards.map(card => ({
    ...card,
    posicao: { x: minX, y: card.posicao.y },
  }));
}

/**
 * Alinha cards selecionados à direita (mesmo X final)
 */
export function alignRight(cards: CardData[]): CardData[] {
  if (cards.length === 0) return cards;
  const maxX = Math.max(...cards.map(c => c.posicao.x + getCardDimensions(c).width));
  return cards.map(card => ({
    ...card,
    posicao: { x: maxX - getCardDimensions(card).width, y: card.posicao.y },
  }));
}

/**
 * Alinha cards selecionados ao topo (mesmo Y)
 */
export function alignTop(cards: CardData[]): CardData[] {
  if (cards.length === 0) return cards;
  const minY = Math.min(...cards.map(c => c.posicao.y));
  return cards.map(card => ({
    ...card,
    posicao: { x: card.posicao.x, y: minY },
  }));
}

/**
 * Alinha cards selecionados à base (mesmo Y final)
 */
export function alignBottom(cards: CardData[]): CardData[] {
  if (cards.length === 0) return cards;
  const maxY = Math.max(...cards.map(c => c.posicao.y + getCardDimensions(c).height));
  return cards.map(card => ({
    ...card,
    posicao: { x: card.posicao.x, y: maxY - getCardDimensions(card).height },
  }));
}

/**
 * Centraliza cards horizontalmente
 */
export function centerHorizontal(cards: CardData[]): CardData[] {
  if (cards.length === 0) return cards;
  const bbox = getBoundingBox(cards);
  const centerX = (bbox.minX + bbox.maxX) / 2;
  
  return cards.map(card => {
    const { width } = getCardDimensions(card);
    return {
      ...card,
      posicao: { x: centerX - width / 2, y: card.posicao.y },
    };
  });
}

/**
 * Centraliza cards verticalmente
 */
export function centerVertical(cards: CardData[]): CardData[] {
  if (cards.length === 0) return cards;
  const bbox = getBoundingBox(cards);
  const centerY = (bbox.minY + bbox.maxY) / 2;
  
  return cards.map(card => {
    const { height } = getCardDimensions(card);
    return {
      ...card,
      posicao: { x: card.posicao.x, y: centerY - height / 2 },
    };
  });
}
